import React, { useState } from 'react';
import { makeStyles, Dialog, DialogContent, Drawer, Button as MuiButton, IconButton } from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import CloseIcon from '@material-ui/icons/Close';
import { designTokens } from '../../styles/designTokens';
import { Loading } from '@cabinet-packages/components';
import SearchInput from '../SearchInput';
import Button from '../Button';
import CollectionPointsFilter from './components/CollectionPointsFilter';
import CollectionPointCard from './components/CollectionPointCard';
import useCollectionPointsData from './hooks/useCollectionPointsData';
import useCollectionPointsFilter from './hooks/useCollectionPointsFilter';

const useStyles = makeStyles({
  dialogPaper: {
    borderRadius: designTokens.borderRadius.modal,
    maxWidth: '1100px',
    width: '100%',
    maxHeight: '90vh',
    margin: designTokens.spacing.lg,
    '@media (max-width: 600px)': {
      margin: designTokens.spacing.sm,
      maxHeight: '95vh',
    }
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${designTokens.spacing.lg} ${designTokens.spacing.xl}`,
    borderBottom: `1px solid ${designTokens.colors.border}`,
    '@media (max-width: 600px)': {
      padding: designTokens.spacing.lg,
    }
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: designTokens.spacing.xs,
  },
  title: {
    ...designTokens.typography.h2,
    margin: 0,
  },
  subtitle: {
    ...designTokens.typography.label,
    margin: 0,
  },
  closeButton: {
    color: designTokens.colors.text.secondary,
    padding: designTokens.spacing.sm,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    }
  },
  searchSection: {
    padding: `${designTokens.spacing.md} ${designTokens.spacing.xl}`,
    borderBottom: `1px solid ${designTokens.colors.border}`,
    '@media (max-width: 600px)': {
      padding: designTokens.spacing.md,
    }
  },
  mobileFilterButton: {
    display: 'none',
    padding: `${designTokens.spacing.md} ${designTokens.spacing.xl}`,
    '@media (max-width: 900px)': {
      display: 'block',
    },
    '@media (max-width: 600px)': {
      padding: designTokens.spacing.md,
    }
  },
  filterButton: {
    ...designTokens.typography.button,
    fontSize: '15px',
    textTransform: 'none',
    backgroundColor: designTokens.colors.primary,
    color: designTokens.colors.text.white,
    padding: `${designTokens.spacing.sm} ${designTokens.spacing.lg}`,
    borderRadius: designTokens.borderRadius.button,
    '&:hover': {
      backgroundColor: designTokens.colors.primaryDark,
    }
  },
  content: {
    padding: '0 !important',
    display: 'grid',
    gridTemplateColumns: '280px 1fr',
    minHeight: '400px',
    maxHeight: 'calc(90vh - 180px)',
    '@media (max-width: 900px)': {
      gridTemplateColumns: '1fr',
    },
    '@media (max-width: 600px)': {
      maxHeight: 'calc(95vh - 160px)',
    }
  },
  filterSection: {
    borderRight: `1px solid ${designTokens.colors.border}`,
    padding: designTokens.spacing.lg,
    overflow: 'auto',
    '@media (max-width: 900px)': {
      display: 'none',
    }
  },
  listSection: {
    overflow: 'auto',
    padding: `0 ${designTokens.spacing.lg}`,
    '@media (max-width: 600px)': {
      padding: `0 ${designTokens.spacing.sm}`,
    }
  },
  emptyState: {
    textAlign: 'center',
    padding: designTokens.spacing.xxxl,
    ...designTokens.typography.body,
    color: designTokens.colors.text.secondary,
  },
  loadingWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '300px',
  },
  drawerContent: {
    padding: designTokens.spacing.lg,
    minWidth: '300px',
  },
  applyButton: {
    marginTop: designTokens.spacing.lg,
  }
});

const CollectionPointsDialog = ({
  open,
  onClose,
  onSelect,
  selectedPunkt = null
}) => {
  const classes = useStyles();
  const [searchValue, setSearchValue] = useState('');
  const [selectedFilters, setSelectedFilters] = useState({});
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const { punkts, loading } = useCollectionPointsData();

  const { filteredPoints, filterCounts } = useCollectionPointsFilter(
    punkts,
    searchValue,
    selectedFilters
  );

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  const handleFilterChange = (filterId, isChecked) => {
    setSelectedFilters(prev => ({
      ...prev,
      [filterId]: isChecked
    }));
  };

  const handleClearAllFilters = () => {
    setSelectedFilters({});
  };

  const handlePunktSelect = (punkt) => {
    onSelect(punkt);
    onClose();
  };

  const activeFiltersCount = Object.values(selectedFilters).filter(v => v).length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      classes={{ paper: classes.dialogPaper }}
    >
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <h2 className={classes.title}>Оберіть відділення</h2>
          <p className={classes.subtitle}>
            Знайдено {filteredPoints.length} {
              filteredPoints.length === 1 ? 'пункт' :
              filteredPoints.length < 5 ? 'пункти' : 'пунктів'
            }
          </p>
        </div>
        <IconButton onClick={onClose} className={classes.closeButton}>
          <CloseIcon />
        </IconButton>
      </div>

      <div className={classes.searchSection}>
        <SearchInput
          value={searchValue}
          onChange={handleSearchChange}
          placeholder="Пошук за адресою пункту"
        />
      </div>

      <div className={classes.mobileFilterButton}>
        <MuiButton
          className={classes.filterButton}
          startIcon={<FilterListIcon />}
          onClick={() => setMobileFiltersOpen(true)}
          fullWidth
        >
          Фільтри
          {activeFiltersCount > 0 && ` (${activeFiltersCount})`}
        </MuiButton>
      </div>

      <DialogContent className={classes.content}>
        <div className={classes.filterSection}>
          <CollectionPointsFilter
            selectedFilters={selectedFilters}
            onFilterChange={handleFilterChange}
            onClearAll={handleClearAllFilters}
            filterCounts={filterCounts}
          />
        </div>

        <div className={classes.listSection}>
          {loading ? (
            <div className={classes.loadingWrapper}>
              <Loading />
            </div>
          ) : filteredPoints.length === 0 ? (
            <div className={classes.emptyState}>
              {searchValue || Object.values(selectedFilters).some(v => v)
                ? 'Не знайдено пунктів за вибраними критеріями'
                : 'Немає доступних пунктів забору'}
            </div>
          ) : (
            filteredPoints.map(punkt => (
              <CollectionPointCard
                key={punkt.idPunkt}
                punkt={punkt}
                onSelect={handlePunktSelect}
                selected={selectedPunkt?.idPunkt === punkt.idPunkt}
              />
            ))
          )}
        </div>
      </DialogContent>

      <Drawer
        anchor="bottom"
        open={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
      >
        <div className={classes.drawerContent}>
          <CollectionPointsFilter
            selectedFilters={selectedFilters}
            onFilterChange={handleFilterChange}
            onClearAll={handleClearAllFilters}
            filterCounts={filterCounts}
          />
          <Button
            variant="primary"
            size="small"
            onClick={() => setMobileFiltersOpen(false)}
            className={classes.applyButton}
          >
            Застосувати
          </Button>
        </div>
      </Drawer>
    </Dialog>
  );
};

export default CollectionPointsDialog;
